<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(title="Bulk update panel claim status" :loading="isLoading" @back="onCloseDialog")
        template(#actions)
          .d-flex.align-center(style="gap: 12px")
            yz-btn(size="lg" text label="Cancel" @click="onCancelDialog")
            yz-tooltip(v-if="!canSave" :text="saveButtonTooltip" direction="left")
              yz-btn(size="lg" label="Save" :disabled="!canSave" :loading="isLoading" @click="onSaveDialog")
            yz-btn(v-else size="lg" label="Save" :loading="isLoading" @click="onSaveDialog")
      yz-dialog-fullscreen-content.pa-6
        // Header Summary Section
        yz-card.pa-4.mb-6(color="yz-neutral-300")
          v-row(no-gutters align="center")
            v-col(cols="auto")
              .d-flex.align-center
                .yz-medium-body-sm.text-neutral-700 {{ displayPanelName }}
                yz-divider.mx-2(vertical, color="yz-neutral-400")
                .yz-medium-body-xs.text-neutral-500 {{ selectedItems.length }} panel claim{{ selectedItems.length > 1 ? 's' : '' }}
              .yz-heading-6.text-neutral-700.mt-2 {{ totalSubmittedAmount | displayPrice($currency) }}
            v-spacer
            v-col(cols="auto")
              .d-flex.align-center
                yz-btn(size="lg" outlined background-color="white" label="Set received date for all panel claims" @click="onSetAllReceivedDate")
                yz-divider.mx-4(vertical color="yz-neutral-400")
                .text-right
                  .yz-medium-body-sm.text-neutral-700.mb-1 Outstanding amount
                  .yz-heading-6.text-success-500 {{ 0 | displayPrice($currency) }}
        // Claims Table
        yz-card(outlined)
          .pa-4
            yz-data-table(:headers="tableHeaders" :items="claimsData" :items-per-page="pageSize" :page="currentPage" disable-sort)
              template(v-slot:item.amount="{ item }")
                .yz-medium-body-sm {{ item.amount }}
              template(v-slot:item.claimNo="{ item }")
                .yz-medium-body-sm {{ item.claimNo }}
              template(v-slot:item.panel="{ item }")
                yz-badge(border size="sm") {{ item.panel }}
              template(v-slot:item.status="{ item }")
                claim-status-badge(:status="item.status" size="sm")
              template(v-slot:item.receivedDate="{ item }")
                yz-text-field.my-2(:value="formatDate(item.receivedDate)" placeholder="Select received date" prepend-inner-icon="$yzi-calendar" readonly clickable clearable size="md" required :rules="dateRules" required-error-message="Date is required" @click="openDatePicker(item)" @click:clear="item.receivedDate = null")
              template(v-slot:item.receivedAmount="{ item }")
                yz-text-field.my-2(v-model="item.receivedAmountDisplay" type="text" size="md" readonly, background-color="yz-neutral-200")
              template(v-slot:item.adminFee="{ item }")
                .yz-medium-body-sm {{ item.adminFee > 0 ? '- ' : '' }}{{ item.adminFee | displayPrice($currency) }}
              template(v-slot:item.rejectedAmount="{ item }")
                .yz-medium-body-sm {{ item.rejectedAmount > 0 ? '- ' : '' }}{{ item.rejectedAmount | displayPrice($currency) }}
              template(v-slot:item.actions="{ item }")
                yz-btn(label="Assigned" size="sm" outlined :disabled="true")
            // Pagination
            .d-flex.justify-end.mt-4
              yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="item" arrow)

    // Set Received Date Dialog
    set-date-dialog(v-model="showSetReceivedDateDialog" status="received" @confirmed="onReceivedDateConfirmed" @canceled="showSetReceivedDateDialog = false")

    // Individual Date Picker Dialog
    yz-dialog(v-if="currentEditingItem" v-model="showDatePickerDialog" max-width="380px")
      yz-card
        yz-dialog-normal-content.px-2.pt-2.pb-0
          yz-date-picker(v-model="currentEditingItem.receivedDate" scrollable, @change="showDatePickerDialog = false")

    // Bulk Change Status Confirmation Dialog
    bulk-change-claim-status-confirmation-dialog(v-if="showChangeStatusDialog" v-model="showChangeStatusDialog" current-status="approved" new-status="received" @confirmed="handleBulkUpdate" @canceled="showChangeStatusDialog = false")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'
import SetDateDialog from './SetDateDialog.vue'
import BulkChangeClaimStatusConfirmationDialog from '@/components/clinic/panel/dialogs/BulkChangeClaimStatusConfirmationDialog.vue'

export default {
  components: { ClaimStatusBadge, SetDateDialog, BulkChangeClaimStatusConfirmationDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showSetReceivedDateDialog: false,
      showDatePickerDialog: false,
      showChangeStatusDialog: false,
      currentEditingItem: null,
      currentPage: 1,
      pageSize: 10,
      isLoading: false,
      tableHeaders: [
        { text: 'Amount', value: 'amount' },
        { text: 'Claim No', value: 'claimNo' },
        { text: 'Panel', value: 'panel' },
        { text: 'Status', value: 'status' },
        { text: 'Received Date', value: 'receivedDate' },
        { text: 'Received Amount', value: 'receivedAmount' },
        { text: 'Admin Fee', value: 'adminFee' },
        { text: 'Rejected Amount', value: 'rejectedAmount' },
        { text: 'Assign balance', value: 'actions', sortable: false, align: 'end', width: '200px' }
      ],
      claimsData: []
    }
  },
  mounted() {
    this.initializeClaimsData()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    totalCount() {
      return this.claimsData.length
    },
    panelBreakdown() {
      const breakdown = {}
      this.selectedItems.forEach(claim => {
        const panelName = claim.panel?.name || 'N/A'
        if (!breakdown[panelName]) {
          breakdown[panelName] = {
            name: panelName,
            count: 0
          }
        }
        breakdown[panelName].count++
      })
      return Object.values(breakdown)
    },
    primaryPanel() {
      if (this.panelBreakdown.length === 1) {
        const panel = this.panelBreakdown[0]
        return panel.name !== 'N/A' ? panel.name : null
      }
      return null
    },
    displayPanelName() {
      return this.primaryPanel || 'Multiple Panels'
    },
    totalSubmittedAmount() {
      return this.selectedItems.reduce((sum, claim) => sum + (claim.submitted_amount || 0), 0)
    },
    canSave() {
      // Check if all required dates are filled
      const hasInvalidDates = this.claimsData.some(claim => !claim.receivedDate)
      return !hasInvalidDates
    },
    saveButtonTooltip() {
      const missingDates = this.claimsData.filter(claim => !claim.receivedDate).length
      if (missingDates > 0) {
        return `Please set received dates for ${missingDates} claim(s)`
      }
      return ''
    },
    dateRules() {
      return [
        v => !!v || 'Date is required'
      ]
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    formatDate(dateString) {
      if (!dateString) return ''
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy')
    },
    openDatePicker(item) {
      this.currentEditingItem = item
      this.showDatePickerDialog = true
    },
    initializeClaimsData() {
      this.claimsData = this.selectedItems.map(claim => {
        return {
          // Original claim data
          id: claim.id,
          submittedAmount: claim.submitted_amount || 0,

          // Display data
          amount: this.$options.filters.displayPrice(claim.submitted_amount || 0, this.$currency),
          claimNo: claim.rid || '',
          panel: claim.panel?.name || 'N/A',
          status: 'approved',

          // Editable fields
          receivedDate: null,

          // Readonly fields (from approved claim)
          receivedAmount: claim.approved_amount || 0,
          receivedAmountDisplay: this.$options.filters.displayPrice(claim.approved_amount || 0, this.$currency),
          adminFee: claim.admin_fee || 0,
          adminFeePercentage: claim.admin_fee_percentage || null,
          rejectedAmount: claim.rejected_amount || 0,

          // Balance assignment tracking (always true for approved→received)
          balanceAssigned: true
        }
      })
    },
    onCloseDialog() {
      this.$emit('input', false)
      this.$emit('close')
    },
    onCancelDialog() {
      this.$emit('input', false)
      this.$emit('canceled')
    },
    async onSaveDialog() {
      if (!this.canSave) {
        return
      }

      // Show confirmation dialog
      this.showChangeStatusDialog = true
    },
    async handleBulkUpdate(logNote) {
      this.isLoading = true
      try {
        // Build the claims payload
        const claims = this.claimsData.map(claim => {
          return {
            id: claim.id,
            status: 'received',
            received_date: claim.receivedDate,
            received_amount: claim.receivedAmount,
            admin_fee: claim.adminFee
          }
        })

        // Call bulk update API with log_note
        await this.$axios.post(`/panels/${this.company.slug}/claims/bulk_update`, { claims, log_note: logNote || null })

        await this.showMessage(['success', `${this.claimsData.length} panel claim${this.claimsData.length > 1 ? 's' : ''} updated successfully`])

        this.$emit('confirmed', this.claimsData)
        this.$emit('input', false)
      } catch (error) {
        console.error('Error updating claims:', error)
        await this.showMessage(['error', 'Failed to update claims. Please try again.'])
      } finally {
        this.isLoading = false
        this.showChangeStatusDialog = false
      }
    },
    onSetAllReceivedDate() {
      this.showSetReceivedDateDialog = true
    },
    onReceivedDateConfirmed(date) {
      // Set the received date for all claims
      this.claimsData.forEach(claim => {
        claim.receivedDate = date
      })
      this.showSetReceivedDateDialog = false
    }
  }
}
</script>

<style lang="scss" scoped></style>
